import React, { useState } from 'react';
import { FaPlus, FaTrash, FaMagic, FaUser, FaBriefcase, FaGraduationCap, FaTools, FaProjectDiagram } from 'react-icons/fa';
import { useGemini } from '../../hooks/useGemini';
import { toast } from 'react-hot-toast';

const ResumeForm = ({ data, onUpdateData }) => {
  const [activeSection, setActiveSection] = useState('personalInfo');
  const { generateContent, loading: aiLoading, error: aiError } = useGemini();
  const [generatingSection, setGeneratingSection] = useState('');

  const sections = [
    { id: 'personalInfo', label: 'Personal Information', icon: <FaUser /> },
    { id: 'workExperience', label: 'Work Experience', icon: <FaBriefcase /> },
    { id: 'education', label: 'Education', icon: <FaGraduationCap /> },
    { id: 'skills', label: 'Skills', icon: <FaTools /> },
    { id: 'projects', label: 'Projects', icon: <FaProjectDiagram /> }
  ];

  const handlePersonalInfoChange = (e) => {
    const { name, value } = e.target;
    onUpdateData('personalInfo', {
      ...data.personalInfo,
      [name]: value
    });
  };

  const handleArrayFieldAdd = (section, defaultItem) => {
    onUpdateData(section, [...data[section], defaultItem]);
  };

  const handleArrayFieldRemove = (section, index) => {
    onUpdateData(section, data[section].filter((_, i) => i !== index));
  };

  const handleArrayFieldChange = (section, index, field, value) => {
    const newData = [...data[section]];
    newData[index] = { ...newData[index], [field]: value };
    onUpdateData(section, newData);
  };

  const handleAIGenerate = async (section) => {
    try {
      setGeneratingSection(section);
      let prompt = '';
      
      switch(section) {
        case 'workExperience':
          if (!data.workExperience[data.workExperience.length - 1]?.company || !data.workExperience[data.workExperience.length - 1]?.position) {
            toast.error('Please enter company and position first');
            return;
          }
          prompt = `Generate a professional work experience description for the following role:
            Company: ${data.workExperience[data.workExperience.length - 1].company}
            Position: ${data.workExperience[data.workExperience.length - 1].position}
            Focus on key achievements and responsibilities.`;
          break;
        case 'education':
          if (!data.education[data.education.length - 1]?.school || !data.education[data.education.length - 1]?.degree) {
            toast.error('Please enter school and degree first');
            return;
          }
          prompt = `Generate a concise education description for:
            School: ${data.education[data.education.length - 1].school}
            Degree: ${data.education[data.education.length - 1].degree}`;
          break;
        case 'projects':
          if (!data.projects[data.projects.length - 1]?.name || !data.projects[data.projects.length - 1]?.technologies) {
            toast.error('Please enter project name and technologies first');
            return;
          }
          prompt = `Generate a compelling project description for:
            Project: ${data.projects[data.projects.length - 1].name}
            Technologies: ${data.projects[data.projects.length - 1].technologies}`;
          break;
        case 'skills':
          if (data.skills.length === 0) {
            toast.error('Please add at least one skill first');
            return;
          }
          prompt = `Suggest 5 additional relevant technical skills based on the following skills:
            ${data.skills.join(', ')}`;
          break;
      }

      const generatedContent = await generateContent(prompt);
      
      if (aiError) {
        toast.error(aiError);
        return;
      }

      if (section === 'skills') {
        const newSkills = generatedContent.split(',').map(skill => skill.trim());
        onUpdateData('skills', [...new Set([...data.skills, ...newSkills])]);
        toast.success('Generated new skill suggestions!');
      } else {
        const lastIndex = data[section].length - 1;
        handleArrayFieldChange(section, lastIndex, 'description', generatedContent);
        toast.success('Generated new content!');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Failed to generate content. Please try again.');
    } finally {
      setGeneratingSection('');
    }
  };

  return (
    <div className="space-y-8">
      {/* Section Tabs */}
      <div className="flex flex-wrap gap-4">
        {sections.map(section => (
          <button
            key={section.id}
            onClick={() => setActiveSection(section.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all duration-300 
                      ${activeSection === section.id 
                        ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg shadow-blue-500/25 transform -translate-y-1' 
                        : 'bg-[#2A2A2A] text-gray-400 hover:bg-[#333333] hover:text-gray-300'}`}
          >
            <span className="text-lg">{section.icon}</span>
            {section.label}
          </button>
        ))}
      </div>

      {/* Personal Information Section */}
      {activeSection === 'personalInfo' && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {Object.entries(data.personalInfo).map(([field, value]) => (
              <div key={field} className="space-y-2">
                <label className="block text-gray-300 text-sm font-medium">
                  {field.charAt(0).toUpperCase() + field.slice(1).replace(/([A-Z])/g, ' $1')}
                </label>
                <input
                  type="text"
                  name={field}
                  value={value}
                  onChange={handlePersonalInfoChange}
                  className="w-full px-4 py-2 rounded-lg bg-[#2A2A2A] border border-gray-700 text-white 
                           placeholder-gray-500 focus:outline-none focus:border-blue-500 focus:ring-1 
                           focus:ring-blue-500 transition-colors duration-200"
                  placeholder={`Enter your ${field.toLowerCase()}`}
                />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Work Experience Section */}
      {activeSection === 'workExperience' && (
        <div className="space-y-6">
          {data.workExperience.map((exp, index) => (
            <div key={index} className="p-6 bg-[#2A2A2A] rounded-xl border border-gray-700 space-y-4 
                                     hover:border-blue-500/50 transition-colors duration-300">
              <div className="flex justify-between items-start">
                <div className="space-y-4 flex-grow">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <input
                      type="text"
                      value={exp.company}
                      onChange={(e) => handleArrayFieldChange('workExperience', index, 'company', e.target.value)}
                      placeholder="Company Name"
                      className="w-full px-4 py-2 rounded-lg bg-[#1A1A1A] border border-gray-700 text-white 
                               placeholder-gray-500 focus:outline-none focus:border-blue-500 focus:ring-1 
                               focus:ring-blue-500 transition-colors duration-200"
                    />
                    <input
                      type="text"
                      value={exp.position}
                      onChange={(e) => handleArrayFieldChange('workExperience', index, 'position', e.target.value)}
                      placeholder="Position"
                      className="w-full px-4 py-2 rounded-lg bg-[#1A1A1A] border border-gray-700 text-white 
                               placeholder-gray-500 focus:outline-none focus:border-blue-500 focus:ring-1 
                               focus:ring-blue-500 transition-colors duration-200"
                    />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <input
                      type="text"
                      value={exp.startDate}
                      onChange={(e) => handleArrayFieldChange('workExperience', index, 'startDate', e.target.value)}
                      placeholder="Start Date"
                      className="w-full px-4 py-2 rounded-lg bg-[#1A1A1A] border border-gray-700 text-white 
                               placeholder-gray-500 focus:outline-none focus:border-blue-500 focus:ring-1 
                               focus:ring-blue-500 transition-colors duration-200"
                    />
                    <input
                      type="text"
                      value={exp.endDate}
                      onChange={(e) => handleArrayFieldChange('workExperience', index, 'endDate', e.target.value)}
                      placeholder="End Date"
                      className="w-full px-4 py-2 rounded-lg bg-[#1A1A1A] border border-gray-700 text-white 
                               placeholder-gray-500 focus:outline-none focus:border-blue-500 focus:ring-1 
                               focus:ring-blue-500 transition-colors duration-200"
                    />
                  </div>
                  <textarea
                    value={exp.description}
                    onChange={(e) => handleArrayFieldChange('workExperience', index, 'description', e.target.value)}
                    placeholder="Job Description"
                    rows={4}
                    className="w-full px-4 py-2 rounded-lg bg-[#1A1A1A] border border-gray-700 text-white 
                             placeholder-gray-500 focus:outline-none focus:border-blue-500 focus:ring-1 
                             focus:ring-blue-500 transition-colors duration-200"
                  />
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleArrayFieldRemove('workExperience', index)}
                    className="p-2 text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-lg transition-colors duration-200"
                  >
                    <FaTrash />
                  </button>
                  <button
                    onClick={() => handleAIGenerate('workExperience')}
                    disabled={aiLoading || generatingSection === 'workExperience'}
                    className="p-2 text-blue-400 hover:text-blue-300 hover:bg-blue-500/10 rounded-lg transition-colors duration-200
                             disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <FaMagic />
                  </button>
                </div>
              </div>
            </div>
          ))}
          <button
            onClick={() => handleArrayFieldAdd('workExperience', {
              company: '',
              position: '',
              startDate: '',
              endDate: '',
              description: ''
            })}
            className="w-full py-3 bg-[#2A2A2A] border border-dashed border-gray-700 rounded-xl text-gray-400
                     hover:bg-[#333333] hover:border-blue-500/50 hover:text-gray-300 transition-all duration-300
                     flex items-center justify-center gap-2"
          >
            <FaPlus />
            Add Work Experience
          </button>
        </div>
      )}

      {/* Education Section */}
      {activeSection === 'education' && (
        <div className="space-y-6">
          {data.education.map((edu, index) => (
            <div key={index} className="p-6 bg-[#2A2A2A] rounded-xl border border-gray-700 space-y-4 
                                     hover:border-blue-500/50 transition-colors duration-300">
              <div className="flex justify-between items-start">
                <div className="space-y-4 flex-grow">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <input
                      type="text"
                      value={edu.school}
                      onChange={(e) => handleArrayFieldChange('education', index, 'school', e.target.value)}
                      placeholder="School Name"
                      className="w-full px-4 py-2 rounded-lg bg-[#1A1A1A] border border-gray-700 text-white 
                               placeholder-gray-500 focus:outline-none focus:border-blue-500 focus:ring-1 
                               focus:ring-blue-500 transition-colors duration-200"
                    />
                    <input
                      type="text"
                      value={edu.degree}
                      onChange={(e) => handleArrayFieldChange('education', index, 'degree', e.target.value)}
                      placeholder="Degree"
                      className="w-full px-4 py-2 rounded-lg bg-[#1A1A1A] border border-gray-700 text-white 
                               placeholder-gray-500 focus:outline-none focus:border-blue-500 focus:ring-1 
                               focus:ring-blue-500 transition-colors duration-200"
                    />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <input
                      type="text"
                      value={edu.startDate}
                      onChange={(e) => handleArrayFieldChange('education', index, 'startDate', e.target.value)}
                      placeholder="Start Date"
                      className="w-full px-4 py-2 rounded-lg bg-[#1A1A1A] border border-gray-700 text-white 
                               placeholder-gray-500 focus:outline-none focus:border-blue-500 focus:ring-1 
                               focus:ring-blue-500 transition-colors duration-200"
                    />
                    <input
                      type="text"
                      value={edu.endDate}
                      onChange={(e) => handleArrayFieldChange('education', index, 'endDate', e.target.value)}
                      placeholder="End Date"
                      className="w-full px-4 py-2 rounded-lg bg-[#1A1A1A] border border-gray-700 text-white 
                               placeholder-gray-500 focus:outline-none focus:border-blue-500 focus:ring-1 
                               focus:ring-blue-500 transition-colors duration-200"
                    />
                  </div>
                  <textarea
                    value={edu.description}
                    onChange={(e) => handleArrayFieldChange('education', index, 'description', e.target.value)}
                    placeholder="Education Description"
                    rows={4}
                    className="w-full px-4 py-2 rounded-lg bg-[#1A1A1A] border border-gray-700 text-white 
                             placeholder-gray-500 focus:outline-none focus:border-blue-500 focus:ring-1 
                             focus:ring-blue-500 transition-colors duration-200"
                  />
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleArrayFieldRemove('education', index)}
                    className="p-2 text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-lg transition-colors duration-200"
                  >
                    <FaTrash />
                  </button>
                  <button
                    onClick={() => handleAIGenerate('education')}
                    disabled={aiLoading || generatingSection === 'education'}
                    className="p-2 text-blue-400 hover:text-blue-300 hover:bg-blue-500/10 rounded-lg transition-colors duration-200
                             disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <FaMagic />
                  </button>
                </div>
              </div>
            </div>
          ))}
          <button
            onClick={() => handleArrayFieldAdd('education', {
              school: '',
              degree: '',
              startDate: '',
              endDate: '',
              description: ''
            })}
            className="w-full py-3 bg-[#2A2A2A] border border-dashed border-gray-700 rounded-xl text-gray-400
                     hover:bg-[#333333] hover:border-blue-500/50 hover:text-gray-300 transition-all duration-300
                     flex items-center justify-center gap-2"
          >
            <FaPlus />
            Add Education
          </button>
        </div>
      )}

      {/* Skills Section */}
      {activeSection === 'skills' && (
        <div className="space-y-6">
          <div className="flex flex-wrap gap-2">
            {data.skills.map((skill, index) => (
              <div
                key={index}
                className="group flex items-center gap-2 px-4 py-2 bg-[#2A2A2A] rounded-lg border border-gray-700
                         hover:border-red-500/50 transition-colors duration-300"
              >
                <span className="text-gray-300">{skill}</span>
                <button
                  onClick={() => handleArrayFieldRemove('skills', index)}
                  className="text-red-400 hover:text-red-300 opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                >
                  <FaTrash className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
          <div className="flex gap-4">
            <input
              type="text"
              placeholder="Add a skill"
              onKeyPress={(e) => {
                if (e.key === 'Enter' && e.target.value.trim()) {
                  handleArrayFieldAdd('skills', e.target.value.trim());
                  e.target.value = '';
                }
              }}
              className="flex-grow px-4 py-2 rounded-lg bg-[#1A1A1A] border border-gray-700 text-white 
                       placeholder-gray-500 focus:outline-none focus:border-blue-500 focus:ring-1 
                       focus:ring-blue-500 transition-colors duration-200"
            />
            <button
              onClick={() => handleAIGenerate('skills')}
              disabled={aiLoading || generatingSection === 'skills'}
              className="px-4 py-2 bg-[#2A2A2A] text-blue-400 rounded-lg hover:bg-blue-500/10 
                       transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed
                       flex items-center gap-2"
            >
              <FaMagic />
              Suggest Skills
            </button>
          </div>
        </div>
      )}

      {/* Projects Section */}
      {activeSection === 'projects' && (
        <div className="space-y-6">
          {data.projects.map((project, index) => (
            <div key={index} className="p-6 bg-[#2A2A2A] rounded-xl border border-gray-700 space-y-4 
                                     hover:border-blue-500/50 transition-colors duration-300">
              <div className="flex justify-between items-start">
                <div className="space-y-4 flex-grow">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <input
                      type="text"
                      value={project.name}
                      onChange={(e) => handleArrayFieldChange('projects', index, 'name', e.target.value)}
                      placeholder="Project Name"
                      className="w-full px-4 py-2 rounded-lg bg-[#1A1A1A] border border-gray-700 text-white 
                               placeholder-gray-500 focus:outline-none focus:border-blue-500 focus:ring-1 
                               focus:ring-blue-500 transition-colors duration-200"
                    />
                    <input
                      type="text"
                      value={project.technologies}
                      onChange={(e) => handleArrayFieldChange('projects', index, 'technologies', e.target.value)}
                      placeholder="Technologies Used"
                      className="w-full px-4 py-2 rounded-lg bg-[#1A1A1A] border border-gray-700 text-white 
                               placeholder-gray-500 focus:outline-none focus:border-blue-500 focus:ring-1 
                               focus:ring-blue-500 transition-colors duration-200"
                    />
                  </div>
                  <textarea
                    value={project.description}
                    onChange={(e) => handleArrayFieldChange('projects', index, 'description', e.target.value)}
                    placeholder="Project Description"
                    rows={4}
                    className="w-full px-4 py-2 rounded-lg bg-[#1A1A1A] border border-gray-700 text-white 
                             placeholder-gray-500 focus:outline-none focus:border-blue-500 focus:ring-1 
                             focus:ring-blue-500 transition-colors duration-200"
                  />
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleArrayFieldRemove('projects', index)}
                    className="p-2 text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-lg transition-colors duration-200"
                  >
                    <FaTrash />
                  </button>
                  <button
                    onClick={() => handleAIGenerate('projects')}
                    disabled={aiLoading || generatingSection === 'projects'}
                    className="p-2 text-blue-400 hover:text-blue-300 hover:bg-blue-500/10 rounded-lg transition-colors duration-200
                             disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <FaMagic />
                  </button>
                </div>
              </div>
            </div>
          ))}
          <button
            onClick={() => handleArrayFieldAdd('projects', {
              name: '',
              technologies: '',
              description: ''
            })}
            className="w-full py-3 bg-[#2A2A2A] border border-dashed border-gray-700 rounded-xl text-gray-400
                     hover:bg-[#333333] hover:border-blue-500/50 hover:text-gray-300 transition-all duration-300
                     flex items-center justify-center gap-2"
          >
            <FaPlus />
            Add Project
          </button>
        </div>
      )}
    </div>
  );
};

export default ResumeForm;
